import { useQuery } from '@tanstack/react-query';
import { n8nService } from '../components/core/services/n8n.service';
import { queryKeys } from '../services/query/queryKeys';
import { buildSellerProfile, getSellerEnrichedAt } from '../utils/sellerProfile';

/**
 * Seller profiles change only on explicit enrichment, so one fetch serves the
 * Sellers card grid + details modal for a full day, AND survives page reloads
 * (the ['sellers'] family is persisted by QueryProvider and exempt from the
 * boot invalidation). The Sellers-tab Refresh button invalidates the family
 * explicitly when fresh data is wanted.
 *
 * EXCEPTION — a freshly-added seller has NO enrichment yet: add_seller returns
 * immediately with basic fields while the lvl1 enrichment pipeline fills the
 * profile minutes later in the background. A profile captured in that window
 * (no `last_enriched`) must NOT count as fresh for 24h, or the modal would
 * show "No information available yet" until the next day (the Refresh button
 * is the only other escape hatch). staleTime is therefore dynamic: 0 until
 * the cached entry carries an enrichment timestamp, 24h after that.
 */
export const SELLER_PROFILE_STALE_TIME = 24 * 60 * 60 * 1000;
export const SELLER_PROFILE_GC_TIME = 24 * 60 * 60 * 1000;

/** Fetch a seller's enriched profile; shared cache entry per lvl1 id. */
export const fetchSellerProfile = (seller: { id: string; label: string }) =>
  async () => {
    const response = await n8nService.getLvl1Profile({ lvl1_id: seller.id, client_email: '' });
    if (!response.success) throw new Error('Could not load the seller profile.');
    return response;
  };

/** Cache the endpoint response; derive a seller-specific view model for the UI. */
export function useSellerProfile(
  seller: { id: string; label: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.sellers.profile(seller.id),
    queryFn: fetchSellerProfile(seller),
    select: (response) => buildSellerProfile(response.data, seller),
    enabled: options?.enabled ?? true,
    // Unenriched profiles refetch on every mount (async enrichment lands);
    // enriched ones keep the full-day cache.
    staleTime: (query) =>
      getSellerEnrichedAt(query.state.data?.data) ? SELLER_PROFILE_STALE_TIME : 0,
    gcTime: SELLER_PROFILE_GC_TIME,
    retry: 1,
  });
}
